// defenses-info.ts — side info panels for the Defenses tab. Show WHERE each defense comes from
// with full provenance, read from window.FateTide.casterCache (NO DB ping).
//
// Source attribution (defenseSourceChains): every defense leaf (a damage type, an immune-to
// status, or a permanent status) is traced to its origin by scanning the blob:
//   - own pristine item_layer_types[layer]            → source = "host"
//   - creature_type / sub_type / template carriers    → source = the carrier (e.g. "Construct")
//   - permanent statuses (status_effect_permanent)    → source = the status (e.g. "Invulnerable")
//   - inline structured source_* on the computed entry (dynamic/user-choice/shapeshift) → fallback
// Category panel: one row per leaf → "from <source(s)>". Per-type panel: the full TRACE crumb
// (via-layer › source › leaf), e.g. "Permanent Status Effect › Invulnerable › Acid" — the header
// is the category (click → all breakdowns), no chevron.
//
// Each panel renders into #div_refresh_side_info and registers with setActiveInfoPanel for WS live-refresh.

import type { CachedCaster, ItemLikeRecord } from './types';
import { resolveLineLabel } from './source-line-block';
import { setActiveInfoPanel } from './active-panel';
import { findFullNestedItem } from '../object-card/find-cached-item';

interface DefenseSource {
    source_item_id?: string;
    source_name?: string;
    source_kind?: string;
}

interface DefenseEntry extends DefenseSource {
    damage_type: string;
    all_sources?: DefenseSource[];
}

/** A provenance chain: the host holds the leaf VIA <viaLayer>, sourced from <source>. */
interface ProvChain {
    viaLayer: string;     // 'host' | 'status_effect_permanent' | 'creature_type' | 'creature_sub_type' | 'creature_template' | <inline kind>
    sourceId: string;     // '' for host
    sourceName: string;   // 'host' | carrier/status name
    sourceKind: string;   // 'host' | 'creature_type' | 'status_effect' | …
}

const DEFENSE_META: Record<string, { label: string; badge: string; grad: string }> = {
    damage_type_resistance: {
        label: 'Damage Resistance', badge: 'bg-primary text-dark',
        grad: 'linear-gradient(90deg, rgba(220,53,69,.40), rgba(220,53,69,.04))',
    },
    damage_type_immunity: {
        label: 'Damage Immunity', badge: 'bg-secondary text-dark',
        grad: 'linear-gradient(90deg, rgba(255,255,255,.30), rgba(255,255,255,.03))',
    },
    damage_type_vulnerability: {
        label: 'Damage Vulnerability', badge: 'bg-danger text-dark',
        grad: 'linear-gradient(90deg, rgba(0,0,0,.85), rgba(130,130,130,.10))',
    },
    immune_status_effect: {
        label: 'Status-Effect Immunity', badge: 'bg-warning text-dark',
        grad: 'linear-gradient(90deg, rgba(255,193,7,.30), rgba(255,193,7,.03))',
    },
    status_effect_permanent: {
        label: 'Permanent Effects', badge: 'bg-info text-dark',
        grad: 'linear-gradient(90deg, rgba(13,202,240,.28), rgba(13,202,240,.03))',
    },
    contesting_roll_adv_vs_status_effect: {
        label: 'Contest Advantage vs Status', badge: 'bg-light text-dark',
        grad: 'linear-gradient(90deg, rgba(25,135,84,.32), rgba(25,135,84,.03))',
    },
    contesting_roll_dis_vs_status_effect: {
        label: 'Contest Disadvantage vs Status', badge: 'bg-dark',
        grad: 'linear-gradient(90deg, rgba(132,32,41,.50), rgba(132,32,41,.05))',
    },
};

// via-layer → human label for the trace crumb.
const VIA_LABEL: Record<string, string> = {
    host: 'Host',
    status_effect_permanent: 'Permanent Status Effect',
    creature_type: 'Creature Type',
    creature_sub_type: 'Creature Sub-Type',
    creature_template: 'Creature Template',
};

// Damage-defense layers carry inline structured source_* (the scan-fallback uses them).
const DAMAGE_LAYERS = new Set(['damage_type_resistance', 'damage_type_immunity', 'damage_type_vulnerability']);
// Layers whose computed entries carry inline structured source_* ({source_item_id, source_name,
// source_kind, all_sources}) — the trace supplements the carrier/permanent scan with these so
// feat/passive/permanent/self grants (which the carrier scan can't see) are attributed. As of
// 2026-06-21 the status-family defenses are SOURCED structs too, so they join this set.
const INLINE_SOURCE_LAYERS = new Set([
    'damage_type_resistance', 'damage_type_immunity', 'damage_type_vulnerability',
    'immune_status_effect',
    'contesting_roll_adv_vs_status_effect', 'contesting_roll_dis_vs_status_effect',
]);
// Permanent statuses grant these (they don't grant status_effect_permanent itself).
const PERM_GRANT_SECTIONS: ReadonlyArray<{ layer: string; label: string }> = [
    { layer: 'damage_type_immunity', label: 'Damage Immunity' },
    { layer: 'damage_type_resistance', label: 'Damage Resistance' },
    { layer: 'damage_type_vulnerability', label: 'Damage Vulnerability' },
    { layer: 'immune_status_effect', label: 'Immune to Status Effect' },
];

function metaFor(layer: string): { label: string; badge: string; grad: string } {
    return DEFENSE_META[layer] ?? { label: 'Defense', badge: 'bg-secondary', grad: '' };
}

// ── Name resolution ───────────────────────────────────────────────────────────
/** The icon registry's tooltip is the display name for anything iconMap can render (e.g. damage
 *  types, which aren't UUID-keyed in any blob catalog yet always have an icon). */
function iconRegistryName(id: string): string {
    const reg = window.FT_ICON_REGISTRY;
    if (!reg) return '';
    const e = reg[id] || reg[id.toUpperCase()] || reg[id.toLowerCase()];
    const t = e && typeof e.tooltip === 'string' ? e.tooltip : '';
    return (t !== '' && t !== 'Unknown') ? t : '';
}

/** UUID → display name: real catalog item_name → icon-registry tooltip → blob-local label. */
function nameOf(caster: CachedCaster, id: string): string {
    return ftItemNameById(id) || iconRegistryName(id) || resolveLineLabel(caster, id) || 'Unknown';
}

// ── Catalog full-nest readers ───────────────────────────────────────────────────
/** Full-nest node (with item_layer_types) for an id — the riding catalog when it carries the nest,
 *  else resolved across ALL cache roots via findFullNestedItem. Mirrors the reader's carrier-ref
 *  resolution (_ftResolveCarrierRef → ftFindFullNestedItem): a per-creature blob is often lean
 *  in-encounter — the carrier/status full nest lives in the encounter cache, not caster.catalog. */
function itemLikeRecord(value: unknown): ItemLikeRecord | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as ItemLikeRecord
        : null;
}

function catalogNode(caster: CachedCaster, id: string): ItemLikeRecord | null {
    const cat = caster.catalog;
    const ride = cat?.[id] ?? cat?.[id.toUpperCase()] ?? cat?.[id.toLowerCase()] ?? null;
    if (ride?.item_layer_types) return ride;
    const full = itemLikeRecord(findFullNestedItem(id));
    if (full?.item_layer_types) return full;
    return ride ?? full;
}

/** snake_case → Title Case (for via-layer labels not in VIA_LABEL). */
function titleCase(s: string): string {
    return String(s).replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
}

/** node.item_layer_types[layer] → list of UUIDs (structured {damage_type} / shell / bare tolerant).
 *  Reads the node's OWN item_layer_types only (no carrier walk) — used to attribute a specific source. */
function nodeLayerUuids(node: ItemLikeRecord | null, layer: string): string[] {
    const ilt = node?.item_layer_types?.[layer];
    if (ilt == null) return [];
    const arr = Array.isArray(ilt) ? ilt : [ilt];
    const out: string[] = [];
    for (const e of arr) {
        if (e && typeof e === 'object') {
            const record = itemLikeRecord(e);
            const v = record?.damage_type ?? record?.items?.item_id ?? record?.item_id ?? record?.item_layer_effect ?? '';
            const s = String(Array.isArray(v) ? v[0] : v);
            if (s !== '') out.push(s.includes('|') ? (s.split('|')[0] ?? s) : s);
        } else if (typeof e === 'string' && e !== '') {
            out.push(e);
        }
    }
    return out;
}

/** layer_list_entry_uuid twin. */
function entryUuid(entry: unknown): string {
    if (entry == null) return '';
    if (typeof entry === 'string') return entry.trim();
    if (typeof entry === 'number') return String(entry);
    if (Array.isArray(entry)) return entryUuid(entry[0]);
    if (typeof entry === 'object') {
        const o = entry as ItemLikeRecord;
        const raw = o.items?.item_id ?? o.item_id ?? o.damage_type ?? o.item_layer_effect ?? '';
        return String(Array.isArray(raw) ? raw[0] : (raw ?? '')).trim();
    }
    return '';
}

function defenseEntries(caster: CachedCaster, layer: string): DefenseEntry[] {
    const out: DefenseEntry[] = [];
    for (const raw of ftLayerEntries(caster, layer) as unknown[]) {
        if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
            const o = raw as ItemLikeRecord;
            const dt = typeof o.damage_type === 'string' ? o.damage_type : '';
            if (dt !== '') { out.push({ ...o, damage_type: dt } as DefenseEntry); continue; }
            // SOURCED status/contesting struct ({item_layer_effect, source_*}) — normalize the leaf
            // onto `damage_type` so the shared source machinery resolves it, while PRESERVING the
            // inline source_* / all_sources so entrySources() can surface the feat/passive/self origin.
            const leaf = entryUuid(o);
            if (leaf !== '') { out.push({ ...(o as object), damage_type: leaf } as DefenseEntry); continue; }
        }
        const id = entryUuid(raw);
        if (id !== '') out.push({ damage_type: id });
    }
    return out;
}

function entrySources(entry: DefenseEntry): DefenseSource[] {
    const all: DefenseSource[] = [
        { source_item_id: entry.source_item_id, source_name: entry.source_name, source_kind: entry.source_kind },
        ...(entry.all_sources ?? []),
    ].filter((s) => (s.source_name ?? '') !== '' || (s.source_item_id ?? '') !== '');
    const seen = new Set<string>();
    const out: DefenseSource[] = [];
    for (const s of all) {
        const key = String(s.source_item_id ?? s.source_name ?? '');
        if (key === '' || seen.has(key)) continue;
        seen.add(key);
        out.push(s);
    }
    return out;
}

// ── Provenance ────────────────────────────────────────────────────────────────
/** Unique leaf ids for a defense layer (damage types / status ids / permanent-status ids). */
function defenseLeafIds(caster: CachedCaster, layer: string): string[] {
    const out: string[] = [];
    const seen = new Set<string>();
    for (const raw of ftLayerEntries(caster, layer) as unknown[]) {
        const record = itemLikeRecord(raw);
        const id = (record && typeof record.damage_type === 'string' && record.damage_type)
            ? record.damage_type
            : entryUuid(raw);
        if (id === '') continue;
        const u = id.toUpperCase();
        if (seen.has(u)) continue;
        seen.add(u);
        out.push(id);
    }
    return out;
}

/** Resolve a carrier/permanent entry (from ftLayerEntries) to its FULL nest. The entry may already
 *  BE a full node, or a bare/shell ref to resolve across the caches. */
function resolveFull(caster: CachedCaster, raw: unknown): { id: string; node: ItemLikeRecord | null } {
    const record = itemLikeRecord(raw);
    if (record?.item_layer_types) {
        return { id: entryUuid(raw), node: record };
    }
    const id = entryUuid(raw);
    return { id, node: id ? catalogNode(caster, id) : null };
}

/** Trace a defense leaf to all its sources. Enumerate carriers + permanent statuses via the
 *  bridge-aware ftLayerEntries (status_effect_permanent is relocated to computed.creature.abilities,
 *  carrier refs need the carrier resolver) — NOT raw item_layer_types — then resolve each node's
 *  full nest (findFullNestedItem). Anything not granted by a carrier or permanent status is the
 *  creature's own → "host". */
function defenseSourceChains(caster: CachedCaster, layer: string, leafId: string): ProvChain[] {
    const want = leafId.toUpperCase();
    const grants = (node: ItemLikeRecord | null, l: string): boolean =>
        nodeLayerUuids(node, l).some((u) => u.toUpperCase() === want);

    const chains: ProvChain[] = [];
    // creature_type / sub_type / template carriers
    for (const cl of ['creature_template', 'creature_sub_type', 'creature_type']) {
        for (const craw of ftLayerEntries(caster, cl) as unknown[]) {
            const { id, node } = resolveFull(caster, craw);
            if (node && grants(node, layer)) {
                chains.push({ viaLayer: cl, sourceId: id, sourceName: nameOf(caster, id), sourceKind: cl });
            }
        }
    }
    // permanent statuses grant damage / status immunities (not status_effect_permanent itself)
    if (layer !== 'status_effect_permanent') {
        for (const praw of ftLayerEntries(caster, 'status_effect_permanent') as unknown[]) {
            const { id, node } = resolveFull(caster, praw);
            if (node && grants(node, layer)) {
                chains.push({ viaLayer: 'status_effect_permanent', sourceId: id, sourceName: nameOf(caster, id), sourceKind: 'status_effect' });
            }
        }
    }
    // supplement: inline structured source_* (feat / passive / permanent / self / dynamic / user-choice
    // / shapeshift grants the carrier scan can't see) for every layer that carries them — skip sources
    // already found by the scan.
    if (INLINE_SOURCE_LAYERS.has(layer)) {
        const entry = defenseEntries(caster, layer).find((e) => e.damage_type.toUpperCase() === want);
        for (const s of entry ? entrySources(entry) : []) {
            const sid = String(s.source_item_id ?? '');
            if (sid === '' || chains.some((c) => c.sourceId.toUpperCase() === sid.toUpperCase())) continue;
            chains.push({
                viaLayer: s.source_kind || 'status_effect',
                sourceId: sid,
                sourceName: s.source_name || nameOf(caster, sid),
                sourceKind: s.source_kind || 'status_effect',
            });
        }
    }
    // host fallback — granted by neither a carrier nor a permanent status ⇒ the creature's own.
    if (chains.length === 0) {
        chains.push({ viaLayer: 'host', sourceId: '', sourceName: 'host', sourceKind: 'host' });
    }
    return chains;
}

// ── Shared HTML bits ────────────────────────────────────────────────────────────
/** Gradient banner header matching the grid row tint: [category icon] + label, clickable (whole
 *  banner) back to the category breakdown. No chevron (owner: clicking the icon/label = all breakdowns). */
function bannerHeader(layer: string, navOnClick = ''): string {
    const meta = metaFor(layer);
    const attrs = navOnClick !== ''
        ? ` role="button" onclick="${navOnClick}"`
        : '';
    return `<div class="d-flex align-items-center gap-2 px-3 py-2"${attrs} style="background:${meta.grad};">
        <span class="flex-shrink-0 d-inline-flex align-items-center">${iconMap(layer, 28, 28, navOnClick !== '' ? `Show all ${meta.label.toLowerCase()}` : '')}</span>
        <h5 class="my-0">${escapeHtml(meta.label)}</h5>
    </div>`;
}

/** A "from <source>" chip: source icon + name. Host → the `host` glyph + "host" (no link). */
function sourceChip(caster: CachedCaster, chain: ProvChain): string {
    if (chain.sourceKind === 'host' || chain.sourceId === '') {
        return `<span class="d-inline-flex align-items-center gap-1">${iconMap('host', 18, 18)}<span>host</span></span>`;
    }
    const name = escapeHtml(chain.sourceName || nameOf(caster, chain.sourceId));
    const link = checkIfValidUUID(chain.sourceId)
        ? `<a class="link-primary text-decoration-none" href="${escapeHtml(BASE_URL + 'item.php?id=' + chain.sourceId)}" target="_blank" rel="noopener noreferrer">${name}</a>`
        : name;
    return `<span class="d-inline-flex align-items-center gap-1">${iconMap(chain.sourceId, 18, 18)}${link}</span>`;
}

function sourcesInline(caster: CachedCaster, chains: ProvChain[]): string {
    if (chains.length === 0) return '<span class="text-muted">unattributed</span>';
    // dedupe by source so e.g. two carrier passes don't repeat
    const seen = new Set<string>();
    const out: string[] = [];
    for (const c of chains) {
        const key = c.sourceKind === 'host' ? 'host' : (c.sourceId || c.sourceName);
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(sourceChip(caster, c));
    }
    return out.join('<span class="text-muted mx-1">·</span>');
}

function panelShell(headerHtml: string, bodyHtml: string): string {
    return `<div class="border rounded overflow-hidden">
        ${headerHtml}
        <div class="list-group list-group-flush small text-start">${bodyHtml}</div>
    </div>`;
}

function categoryNavOnClick(casterId: string, layer: string): string {
    const cid = escapeHtml(casterId);
    return `if(window.forceOpenSideInfoPanel)forceOpenSideInfoPanel(); if(window.renderDefenseCategoryInfoPanel)renderDefenseCategoryInfoPanel('${cid}','${escapeHtml(layer)}');`;
}

// ── Category panel: one row per defense leaf, "from <source(s)>" ─────────────────
function renderDefenseCategoryTable(caster: CachedCaster, layer: string): string {
    const leaves = defenseLeafIds(caster, layer);
    if (leaves.length === 0) {
        return panelShell(bannerHeader(layer), `<div class="list-group-item text-secondary py-2">None.</div>`);
    }
    const rows = leaves.map((leaf) => {
        const chains = defenseSourceChains(caster, layer, leaf);
        return `<div class="list-group-item d-flex justify-content-between align-items-center gap-2 flex-wrap py-2">
            <span class="d-flex align-items-center gap-2 min-w-0">
                <span class="flex-shrink-0 d-inline-flex align-items-center">${iconMap(leaf, 22, 22)}</span>
                <span class="min-w-0">${escapeHtml(nameOf(caster, leaf))}</span>
            </span>
            <span class="d-flex align-items-center gap-1 flex-wrap justify-content-end small">
                <span class="text-secondary me-1">from</span>${sourcesInline(caster, chains)}
            </span>
        </div>`;
    }).join('');
    return panelShell(bannerHeader(layer), rows);
}

// ── Per-type panel: the full provenance TRACE (via-layer › source › leaf) ────────
function traceCrumb(iconHtml: string, name: string, href: string): string {
    const inner = href !== ''
        ? `<a class="link-primary text-decoration-none" href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">${escapeHtml(name)}</a>`
        : `<span>${escapeHtml(name)}</span>`;
    return `<span class="d-inline-flex align-items-center gap-1">${iconHtml}${inner}</span>`;
}

function renderDefenseTrace(caster: CachedCaster, casterId: string, layer: string, leafId: string): string {
    const chains = defenseSourceChains(caster, layer, leafId);
    const leafName = nameOf(caster, leafId);
    const leafHref = checkIfValidUUID(leafId) ? BASE_URL + 'item.php?id=' + leafId : '';
    const leafCrumb = traceCrumb(iconMap(leafId, 18, 18), leafName, leafHref);
    const arrow = '<span class="text-muted mx-1" aria-hidden="true">›</span>';

    let body: string;
    if (chains.length === 0) {
        body = `<div class="list-group-item d-flex align-items-center py-2 small">${leafCrumb}</div>
            <div class="list-group-item text-secondary py-2">Source not attributed.</div>`;
    } else {
        body = chains.map((c) => {
            const crumbs: string[] = [];
            crumbs.push(traceCrumb(iconMap(c.viaLayer, 18, 18), VIA_LABEL[c.viaLayer] ?? titleCase(c.viaLayer), ''));
            if (c.sourceKind !== 'host' && c.sourceId !== '') {
                crumbs.push(traceCrumb(iconMap(c.sourceId, 18, 18), c.sourceName, checkIfValidUUID(c.sourceId) ? BASE_URL + 'item.php?id=' + c.sourceId : ''));
            }
            crumbs.push(leafCrumb);
            return `<div class="list-group-item d-flex align-items-center flex-wrap py-2 small">${crumbs.join(arrow)}</div>`;
        }).join('');
    }
    return panelShell(bannerHeader(layer, categoryNavOnClick(casterId, layer)), body);
}

/** A permanent (always-on) status: the status (rules link) + what it GRANTS (from .catalog full nest). */
function renderPermanentStatusTable(caster: CachedCaster, casterId: string, statusId: string): string {
    const node = catalogNode(caster, statusId);
    const name = escapeHtml(nameOf(caster, statusId));
    const headRow = checkIfValidUUID(statusId)
        ? `<a class="list-group-item list-group-item-action d-flex align-items-center gap-2 py-2 text-reset text-decoration-none" href="${escapeHtml(BASE_URL + 'item.php?id=' + statusId)}" target="_blank" rel="noopener noreferrer">
            <span class="flex-shrink-0 d-inline-flex align-items-center">${iconMap(statusId, 24, 24)}</span>
            <span class="fw-semibold min-w-0">${name}</span>
        </a>`
        : `<div class="list-group-item d-flex align-items-center gap-2 py-2"><span class="flex-shrink-0 d-inline-flex">${iconMap(statusId, 24, 24)}</span><span class="fw-semibold">${name}</span></div>`;

    let sections = '';
    for (const sec of PERM_GRANT_SECTIONS) {
        const ids = nodeLayerUuids(node, sec.layer);
        if (ids.length === 0) continue;
        const grantRows = ids.map((id) => `<div class="list-group-item d-flex align-items-center gap-2 py-2 ps-4">
            <span class="flex-shrink-0 d-inline-flex align-items-center">${iconMap(id, 20, 20)}</span>
            <span class="min-w-0">${escapeHtml(nameOf(caster, id))}</span>
        </div>`).join('');
        sections += `<div class="list-group-item list-group-item-dark fw-semibold py-1">${escapeHtml(sec.label)}</div>${grantRows}`;
    }
    if (sections === '') sections = `<div class="list-group-item text-secondary py-2">No granted defenses on this status.</div>`;
    return panelShell(bannerHeader('status_effect_permanent', categoryNavOnClick(casterId, 'status_effect_permanent')), headRow + sections);
}

// ── Mount + entry points ─────────────────────────────────────────────────────────
async function renderDefensePanel(
    casterId: string,
    targetId: string,
    render: (caster: CachedCaster) => string,
): Promise<void> {
    const target = document.getElementById(targetId);
    if (!target) return;
    let caster = window.FateTide?.casterCache?.get(casterId) as CachedCaster | undefined;
    if (!caster) {
        target.innerHTML = '<div class="p-3 text-center text-muted">Loading…</div>';
        try {
            await fetchAndCacheCaster(casterId);
        } catch {
            // fetchAndCacheCaster swallows its own errors; fall through to the miss guard.
        }
        caster = window.FateTide?.casterCache?.get(casterId) as CachedCaster | undefined;
    }
    if (!caster) {
        target.innerHTML = '<div class="p-3 text-center text-danger">Could not load creature data.</div>';
        return;
    }
    setActiveInfoPanel(casterId, targetId, render);
    target.innerHTML = render(caster);
}

/** Full category breakdown (damage, status-immunity, or permanent-effects) — one row per leaf, sourced. */
export function renderDefenseCategoryInfoPanel(casterId: string, layer: string, targetId = 'div_refresh_side_info'): Promise<void> {
    return renderDefensePanel(casterId, targetId, (c) => renderDefenseCategoryTable(c, layer));
}

/** One damage type — full provenance trace (via-layer › source › leaf). */
export function renderDefenseTypeInfoPanel(casterId: string, layer: string, damageTypeId: string, targetId = 'div_refresh_side_info'): Promise<void> {
    return renderDefensePanel(casterId, targetId, (c) => renderDefenseTrace(c, casterId, layer, damageTypeId));
}

/** One status-effect immunity — full provenance trace. */
export function renderStatusImmunityInfoPanel(casterId: string, statusId: string, targetId = 'div_refresh_side_info'): Promise<void> {
    return renderDefensePanel(casterId, targetId, (c) => renderDefenseTrace(c, casterId, 'immune_status_effect', statusId));
}

/** One permanent status — the status + everything it grants. */
export function renderPermanentStatusInfoPanel(casterId: string, statusId: string, targetId = 'div_refresh_side_info'): Promise<void> {
    return renderDefensePanel(casterId, targetId, (c) => renderPermanentStatusTable(c, casterId, statusId));
}
